import { ApolloError, NetworkStatus } from '@apollo/client';
import { PoNumberDisplay } from '@/enums';
import { useCallback, useMemo } from 'react';
import { useGeneratedSalesPortalChatPoNumberDisplayQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-chat-po-number-display.generated';

interface SalesPortalChatPoNumberDisplayModeLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface SalesPortalChatPoNumberDisplayModeLoaderResult {
  error: ApolloError | undefined;
  loading: boolean;
  poNumberDisplayMode: PoNumberDisplay | null;
  refetch: () => Promise<unknown>;
  refetching: boolean;
}

/**
 * Fetches the chat (account) level PO number display override for a
 * restaurant/supplier pair. Skipped when either UUID is missing.
 *
 * @param params.restaurantUUID - Restaurant UUID of the account
 * @param params.supplierUUID - Supplier UUID
 *
 * @returns loading state, error, a refetch that never rejects (failures surface
 *   through `error`), a refetching flag that is true only while that refetch is
 *   in flight, and the chat level poNumberDisplayMode (`null` when the account
 *   has no override)
 */
export const useSalesPortalChatPoNumberDisplayModeLoader = ({
  restaurantUUID,
  supplierUUID,
}: SalesPortalChatPoNumberDisplayModeLoaderParams): SalesPortalChatPoNumberDisplayModeLoaderResult => {
  const { data, error, loading, networkStatus, refetch } =
    useGeneratedSalesPortalChatPoNumberDisplayQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !restaurantUUID || !supplierUUID,
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

  const poNumberDisplayMode = useMemo(() => {
    const chat = data?.businesses_by_pk?.supplier_chats?.[0];
    if (!chat?.po_number_display) {
      return null;
    }
    return chat.po_number_display as PoNumberDisplay;
  }, [data?.businesses_by_pk]);

  const retry = useCallback(() => refetch().catch(() => {}), [refetch]);

  return {
    error,
    loading,
    poNumberDisplayMode,
    refetch: retry,
    refetching: networkStatus === NetworkStatus.refetch,
  };
};
